import { CommonHelperFunctions, type IBalanceStateSnapshot } from "@kuika/kuika-cl-model";
import axios from "axios";
import { gatherScreenContext } from "../../components/design-tools/left-menu/mcp-chat/context-pills/mcp-chat-context";
import { processRawToolActivity } from "./mcp-backend-notifications";
import { extractNewKuikaIds, scheduleStaggerReveal, scheduleUpdateHighlight } from "./mcp-canvas-reveal";
import { McpToolExecutor } from "./mcp-tools";
import { getRequestHeaders, getSdInstance, refreshAuthAndGetHeaders } from "./mcp-tools/helpers";

/**
 * Attach a fresh screen-context snapshot to a tool result so the server can
 * update its cached view of the canvas before the next LLM round. Without
 * this, the server keeps sending stale `editableSource` to the model, which
 * then emits `ui_render({before, after})` calls referencing deleted kuikaIds.
 *
 * Only called when a mutating tool finishes at a batch boundary — mid-batch
 * refreshes would thrash because `editableSource` serialization is not free
 * and the intermediate state is not useful to the LLM.
 */
/**
 * Drain the ScreenProvider's deferred sd dispatch. `_mcpDeferRender` is cleared
 * before a batch-ending tool runs so that a mutating tool's own setScreenDesign
 * propagates. But if the terminating tool is read-only (e.g. a screen-query/
 * check), it never calls setScreenDesign and the stashed sd — which holds
 * earlier batch mutations like a body background-color change — sits unrendered
 * until the next user action or a full refresh. Call this after every batch-end
 * tool execution to guarantee the dispatch.
 */
function flushDeferredMcpSd(): void {
  try {
    getSdInstance().screenProvider.flushDeferredSd();
  } catch {
    // Designer not initialized or mid-transition — no sd to flush.
  }
}

/**
 * True when this call MOVED THE CANVAS to a different screen, whatever else it
 * did. Navigating is not a mutation — nothing on the canvas changed — but it
 * invalidates the server's picture of the canvas just as thoroughly, because
 * that picture is a snapshot taken when the user pressed send and never renewed
 * afterwards.
 *
 * KOA-18136: the server hands the Designer "the kuikaIds you may target" from
 * that snapshot. When the pipeline navigates mid-turn (the wizard task-screen
 * pass does exactly that before handing the screen over), the Designer was told
 * the ids of the screen the USER had open — so every patch it aimed at them was
 * rejected as off-canvas and the turn was spent on rejections. Returning a fresh
 * snapshot with the navigate closes that gap at the source: the call that moves
 * the canvas is the one that says where it landed.
 *
 * `create` is included because creating a screen auto-navigates to it.
 */
function movesCanvas(toolName: string, input: Record<string, any>): boolean {
  if (toolName !== "ui_screen") return false;
  const operation = input?.operation;
  return operation === "navigate" || operation === "create";
}

function attachRefreshedContext(result: any): any {
  try {
    const ctx = gatherScreenContext();
    if (!ctx) return result;
    const refreshed = {
      treeSummary: ctx.treeSummary,
      editableSource: ctx.editableSource,
      selectedComponent: ctx.selectedComponent,
      screenId: ctx.screenId,
      screenName: ctx.screenName
    };
    if (result && typeof result === "object") {
      return { ...result, _refreshedContext: refreshed };
    }
    return { value: result, _refreshedContext: refreshed };
  } catch {
    // Non-fatal — server falls back to the stale snapshot
    return result;
  }
}

interface WsBridgeMessage {
  type: string;
  // Required for every message except top-level pushes (`balance_state`)
  // which have no owning request — those are routed by `type` first.
  requestId: string;
  toolName?: string;
  input?: Record<string, any>;
  toolCallId?: string;
  tabId?: string;
  bridgeToken?: string;
  result?: any;
  isError?: boolean;
  // Batch tool execution fields
  tools?: Array<{ requestId: string; toolName: string; input?: Record<string, any>; toolCallId?: string }>;
  results?: Array<{ requestId: string; result: any; isError: boolean }>;
  // Chat-specific fields
  payload?: any;
  event?: string;
  data?: any;
  cleared?: boolean;
  cancelled?: boolean;
  // chat_attach / chat_attach_result
  attach?: ChatAttachClaim[];
  attachResults?: ChatAttachOutcome[];
}

/** One conversation this browser claims as the listener for its chat events. */
export interface ChatAttachClaim {
  /** Durable per-chat-tab conversation id, restored from persisted chat state. */
  sessionId: string;
  /** Chat-tab id, so the panel can match the answer back to the tab. */
  chatTabId: string;
}

/** Server's answer for one claim: the turns it still has running. */
export interface ChatAttachOutcome {
  sessionId: string;
  chatTabId?: string;
  requestIds: string[];
  ok: boolean;
}

export interface ValidationFixItemPayload {
  code: string;
  category: string;
  screenName: string;
  screenId?: string;
  displayItemId?: number;
  kuikaId?: string;
  event?: string;
  parameterName?: string;
  /** Action INSTANCE id (guid) the parameter belongs to — backend-stamped. Lets the
   *  coordinator pick the exact action when an event has several (e.g. pageInit). */
  actionInstanceId?: string;
  message: string;
  type: string;
}

/**
 * How the user resolved a pending interrupt. Mirrors the server's
 * `InterruptDecision` (mcp-chat-server `chat-orchestrator/internals-types.ts`) —
 * the two cannot share a module, so keep them in step.
 *
 * `decide_for_me` carries the answers given so far AND tells the server the user
 * is done answering, so no further question this turn reaches them.
 */
export type InterruptDecision = "approve" | "reject" | "approve_all" | "answer" | "decide_for_me";

export interface ChatRequestPayload {
  session_id?: string;
  tab_id?: string;
  user_id?: string;
  user_name?: string;
  bridge_token?: string;
  message: string;
  screen_context?: any;
  images?: Array<{ base64: string; mediaType: string }>;
  files?: Array<{ base64: string; fileName: string; mediaType: string; fileSize: number }>;
  plan_mode?: boolean;
  ask_mode?: boolean;
  builder_mode?: boolean;
  /** Business Analyst spec approval (KOA-17628) — `message` is the BA's `## Spec:`
   *  markdown sent as the user's prompt; server flips its BA gate to approved. */
  spec_approved?: boolean;
  requested_mode?: "ask" | "designer" | "builder";
  project_id?: string;
  project_name?: string;
  domain_name?: string;
  auth_token?: string;
  traceparent?: string;
  tracestate?: string;
  plan_json?: string;
  history?: any[];
  model_overrides?: {
    complex?: string;
    default?: string;
    cheap?: string;
    braid?: string;
  };
  /** User-facing effort tier (Lite/Economy/Power). Server folds it into model overrides. */
  model_tier?: "lite" | "economy" | "power";
  /** Structured task marker. When set, the server bypasses FDT triage + the
   *  planner and routes straight to the matching handler — the UI already knows
   *  what the action means, so no model has to classify it.
   *  `fix_validations` (KOA-17717) → ValidationFixCoordinator.
   *  `analyze_project` → the discovery walk's dry-run → confirm → run chain. */
  intent?: "fix_validations" | "analyze_project" | "apply_change_delta" | "discard_change_delta";
  validation_fix_items?: ValidationFixItemPayload[];
  /** KOA-18422 — the recorded change delta a plan's Approve/Revise decides on. */
  change_delta_id?: string;
}

/**
 * Context threaded to standalone helper calls (title generation, prompt
 * improvement) so the server can attribute the cost to the user's balance.
 * Mirrors the fields chat requests already carry — without them the
 * server records local telemetry but cannot push durable spend to
 * Backend's KAIChatUsage table.
 */
export interface AuxCallContext {
  session_id?: string;
  project_id?: string;
  project_name?: string;
  domain_name?: string;
  auth_token?: string;
}

function buildAuxPayload(context?: AuxCallContext): AuxCallContext {
  if (!context) return {};
  const out: AuxCallContext = {};
  if (context.session_id) out.session_id = context.session_id;
  if (context.project_id) out.project_id = context.project_id;
  if (context.project_name) out.project_name = context.project_name;
  if (context.domain_name) out.domain_name = context.domain_name;
  if (context.auth_token) out.auth_token = context.auth_token;
  return out;
}

export interface ChatEventData {
  event: string;
  data: any;
  requestId: string;
}

export type ChatEventCallback = (event: ChatEventData) => void;

/**
 * Authoritative balance snapshot pushed by the server's BalanceManager.
 * Frontend applies snapshots in version order; server is the single
 * source of truth. Wire shape lives in `@kuika/kuika-cl-model` so
 * `BalanceChangeSource` additions surface as TS errors on both sides.
 */
export type BalanceStateSnapshot = IBalanceStateSnapshot;
export type BalanceStateCallback = (snapshot: BalanceStateSnapshot) => void;

function isFresherBalanceSnapshot(prev: BalanceStateSnapshot | null, next: BalanceStateSnapshot): boolean {
  if (!prev) return true;
  if (next.domainName !== prev.domainName) return true;
  if (next.projectionId && prev.projectionId && next.projectionId !== prev.projectionId) return true;
  if (next.balanceRevision && prev.balanceRevision && next.balanceRevision !== prev.balanceRevision) return true;
  return next.version > prev.version;
}

function getWsUrl(): string {
  const chatUrl = CommonHelperFunctions.GetAiChatUrl();
  const wsProtocol = chatUrl.startsWith("https") ? "wss" : "ws";
  const urlWithoutProtocol = chatUrl.replace(/^https?:\/\//, "");
  return `${wsProtocol}://${urlWithoutProtocol}/ws`;
}

const WS_URL = getWsUrl();
const BRIDGE_TOKEN_URL = `${CommonHelperFunctions.GetAiChatUrl()}/api/auth/bridge-token`;
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 30000;
const BRIDGE_TOKEN_REFRESH_SKEW_MS = 30_000;

export type McpAnimationZone = "canvas" | "left-panel";

// Category tools: map toolName → { operationName → zone }
// If an operation isn't listed, it doesn't animate.
const CATEGORY_ZONE_MAP: Record<string, Record<string, McpAnimationZone>> = {
  ui_component: {
    add: "canvas",
    configure: "canvas",
    delete: "canvas",
    move: "canvas",
    clone: "canvas",
    rename: "canvas",
    select: "canvas",
    reorder: "canvas",
    swap: "canvas",
    set_responsive_columns: "canvas",
    resize_column: "canvas"
  },
  ui_style: {
    set: "canvas",
    apply_fixes: "canvas"
  },
  ui_history: {
    undo: "canvas",
    redo: "canvas"
  },
  ui_action: {
    add_to_event: "left-panel",
    add_multiple_to_event: "left-panel",
    delete_from_event: "left-panel",
    reorder: "left-panel",
    update_parameters: "left-panel",
    create_conditional: "left-panel"
  }
};

// Non-category tools — flat map (no operation sub-key)
const FLAT_ZONE_MAP: Record<string, McpAnimationZone> = {
  ui_render: "canvas",
  create_sql_action: "left-panel",
  update_sql_action: "left-panel",
  delete_sql_action: "left-panel"
};

export const MCP_ZONE_ACTIVATE = "mcp-zone-activate";
export const MCP_ZONE_DEACTIVATE = "mcp-zone-deactivate";
export const MCP_ZONE_PULSE = "mcp-zone-pulse";
/** Force-reset all zones — unconditionally removes overlays regardless of ref-count */
export const MCP_ZONE_RESET = "mcp-zone-reset";

function getToolZone(toolName: string, input?: Record<string, any>): McpAnimationZone | null {
  // Check category tools (operation-based)
  const opMap = CATEGORY_ZONE_MAP[toolName];
  if (opMap && input?.operation) {
    return opMap[input.operation] || null;
  }
  // Check flat-mapped tools
  return FLAT_ZONE_MAP[toolName] || null;
}

function dispatchZoneEvent(eventName: string, zone: McpAnimationZone): void {
  window.dispatchEvent(new CustomEvent(eventName, { detail: { zone } }));
}

function triggerZonePulse(zone: McpAnimationZone): void {
  dispatchZoneEvent(MCP_ZONE_PULSE, zone);
}

export const MCP_TOOL_ACTIVITY_EVENT = "mcp-tool-activity";

export interface McpToolActivityEvent {
  requestId: string;
  toolCallId?: string;
  tabId?: string;
  toolName: string;
  input: Record<string, any>;
  status: "running" | "completed" | "error" | "blocked";
  isMutating: boolean;
  timestamp: number;
  result?: any;
  error?: string;
  duration?: number;
  /**
   * Final accumulated assistant text for a specialist-run activity (toolName
   * starts with "specialist:"). Set on the completed/error event; undefined
   * for standard tool activities. Consumer (createToolActivity) stores it on
   * ToolActivity.specialistAccumulated for downstream body rendering.
   */
  specialistAccumulated?: string;
}

function dispatchToolActivity(detail: McpToolActivityEvent): void {
  window.dispatchEvent(new CustomEvent(MCP_TOOL_ACTIVITY_EVENT, { detail }));
}

// ── Stagger-reveal batch tracking ──
// Collects kuikaIds of newly added components during a ui_render batch
// (while _mcpDeferRender is active). Cleared and triggered on batch end.
let pendingRevealIds: string[] = [];
let pendingUpdateIds: string[] = [];
let revealSafetyTimer: ReturnType<typeof setTimeout> | null = null;
const REVEAL_SAFETY_TIMEOUT_MS = 30000; // safety reset if batch never completes

/** Extract kuikaIds from tool input (works for both single and batch style/configure calls). */
function extractInputKuikaIds(input: Record<string, any>): string[] {
  const ids: string[] = [];
  if (input.kuikaId) ids.push(String(input.kuikaId));
  // Handle both parsed arrays and stringified JSON
  let components = input.components;
  if (typeof components === "string") {
    try {
      components = JSON.parse(components);
    } catch {
      /* not valid JSON */
    }
  }
  if (Array.isArray(components)) {
    for (const c of components) {
      if (c.kuikaId) ids.push(String(c.kuikaId));
    }
  }
  return ids;
}

function collectRevealIds(toolName: string, input: Record<string, any>, result: any): void {
  // New components → full reveal animation
  if (toolName === "ui_component" && input?.operation === "add" && result && !result.error) {
    const ids = extractNewKuikaIds(result);
    if (ids.length > 0) pendingRevealIds.push(...ids);
  }

  // Style updates on existing components → update highlight
  if (toolName === "ui_style" && input?.operation === "set" && result && !result.error) {
    const ids = extractInputKuikaIds(input);
    if (ids.length > 0) pendingUpdateIds.push(...ids);
  }

  // Configure updates on existing components → update highlight
  if (toolName === "ui_component" && input?.operation === "configure" && result && !result.error) {
    const ids = extractInputKuikaIds(input);
    if (ids.length > 0) pendingUpdateIds.push(...ids);
  }
}

function flushRevealBatch(): void {
  if (revealSafetyTimer) {
    clearTimeout(revealSafetyTimer);
    revealSafetyTimer = null;
  }

  // Deduplicate: new components get full reveal, not update highlight
  const newIdSet = new Set(pendingRevealIds);
  const updateOnly = pendingUpdateIds.filter((id) => !newIdSet.has(id));

  if (pendingRevealIds.length > 0) {
    const ids = [...pendingRevealIds];
    pendingRevealIds = [];
    scheduleStaggerReveal(ids);
  }

  if (updateOnly.length > 0) {
    scheduleUpdateHighlight(updateOnly);
  }

  pendingUpdateIds = [];
}

function startRevealSafetyTimer(): void {
  if (revealSafetyTimer) clearTimeout(revealSafetyTimer);
  revealSafetyTimer = setTimeout(() => {
    // Batch never completed — discard accumulated IDs to prevent leaks
    pendingRevealIds = [];
    pendingUpdateIds = [];
    revealSafetyTimer = null;
  }, REVEAL_SAFETY_TIMEOUT_MS);
}

// Chunked ui_render adds arrive back-to-back WITHOUT _deferRender, so flushing per
// chunk would spawn concurrent scheduleStaggerReveal runs that race on shared style
// nodes and cleanup timers. A short trailing debounce coalesces a burst into ONE
// flush — collectRevealIds keeps accumulating pendingRevealIds across the burst, and
// the single delayed flush reveals them together (one bulk run instead of N).
const REVEAL_FLUSH_DEBOUNCE_MS = 50;
let revealFlushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleCoalescedFlush(): void {
  if (revealFlushTimer) clearTimeout(revealFlushTimer);
  revealFlushTimer = setTimeout(() => {
    revealFlushTimer = null;
    flushRevealBatch();
  }, REVEAL_FLUSH_DEBOUNCE_MS);
}

/**
 * Decide how to flush reveal effects after a single tool call completes.
 * A deferred batch ends with exactly one call, so it flushes immediately (cancelling
 * any debounce still pending from earlier chunks); non-deferred chunks coalesce.
 */
function flushRevealAfterCall(isBatchEnd: boolean, isNonDeferred: boolean): void {
  if (isBatchEnd) {
    if (revealFlushTimer) {
      clearTimeout(revealFlushTimer);
      revealFlushTimer = null;
    }
    flushRevealBatch();
  } else if (isNonDeferred) {
    scheduleCoalescedFlush();
  }
}

/**
 * Schedule `fn` to run after the next paint when the tab is visible, or
 * immediately on the microtask queue when the tab is hidden.
 *
 * `requestAnimationFrame` is *paused entirely* in background tabs, so the
 * old `requestAnimationFrame(() => setTimeout(fn, 0))` pattern blocked
 * tool execution indefinitely while hidden — the server-side per-tool
 * timeout (30s default, 90s for heavy tools) would fire before the rAF
 * callback ever ran. When hidden, there's no paint to yield to anyway,
 * so we skip the rAF wrap entirely.
 */
function yieldToPaint(fn: () => void): void {
  if (typeof document !== "undefined" && document.hidden) {
    queueMicrotask(fn);
    return;
  }
  requestAnimationFrame(() => setTimeout(fn, 0));
}

/**
 * Run `onTick` every `intervalMs` using a Worker-driven timer when
 * available. Web Worker timers are NOT clamped by tab-visibility
 * throttling, so the keepalive keeps firing on schedule even while the
 * tab is in the background. Falls back to `setInterval` (which the
 * browser will throttle to ≥1s when hidden) if Workers are unavailable.
 *
 * Returns a stop function that terminates the timer and worker.
 */
function createBackgroundInterval(intervalMs: number, onTick: () => void): () => void {
  try {
    const workerSource = `
      let id = null;
      self.onmessage = (e) => {
        const d = e.data;
        if (d && d.type === "start") {
          if (id !== null) clearInterval(id);
          id = setInterval(() => self.postMessage("tick"), d.intervalMs);
        } else if (d && d.type === "stop") {
          if (id !== null) { clearInterval(id); id = null; }
        }
      };
    `;
    const blob = new Blob([workerSource], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);
    worker.onmessage = () => {
      try {
        onTick();
      } catch {
        // Tick handler must not crash the worker pump
      }
    };
    worker.postMessage({ type: "start", intervalMs });
    return () => {
      try {
        worker.postMessage({ type: "stop" });
        worker.terminate();
      } finally {
        URL.revokeObjectURL(url);
      }
    };
  } catch {
    const id = setInterval(onTick, intervalMs);
    return () => clearInterval(id);
  }
}

export class McpToolBridge {
  /** Unique identifier for this browser tab — persists across reconnects within the same tab */
  static _tabId: string | null = null;

  /** Get the current tab ID (for including in chat requests) */
  static getTabId(): string {
    if (!McpToolBridge._tabId) {
      McpToolBridge._tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }
    return McpToolBridge._tabId;
  }

  private ws: WebSocket | null = null;
  private reconnectDelay = RECONNECT_DELAY;
  private reconnectTimer: any = null;
  private isDestroyed = false;
  private onConnectionChange?: (connected: boolean) => void;
  private onChatAttachResult?: (outcomes: ChatAttachOutcome[]) => void;
  private bridgeToken: { token: string; expiresAt: number; scopeKey: string } | null = null;
  private forceBridgeTokenRefresh = false;

  // Chat event callbacks keyed by requestId
  private chatEventCallbacks = new Map<string, ChatEventCallback>();

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  setOnConnectionChange(callback: (connected: boolean) => void): void {
    this.onConnectionChange = callback;
  }

  connect(): void {
    if (this.isDestroyed) return;

    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        // connected
        this.reconnectDelay = RECONNECT_DELAY;
        // Register as browser client so the bridge knows to route tool_execute messages here
        // using a short-lived server-signed bridge token instead of a raw userId claim.
        if (!McpToolBridge._tabId) {
          McpToolBridge._tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        }
        this.registerWithBridgeToken();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const message: WsBridgeMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (e) {
          console.error("[McpToolBridge] Failed to parse message:", e);
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        // disconnected
        if (event.code === 4001) {
          this.forceBridgeTokenRefresh = true;
        }
        this.latestBalanceSnapshot = null;
        this.onConnectionChange?.(false);
        this.scheduleReconnect();
      };
    } catch (e) {
      console.error("[McpToolBridge] Connection error:", e);
      this.scheduleReconnect();
    }
  }

  /**
   * Register with the WS server, waiting for userInfo + auth state to be ready.
   * The server requires a valid bridge token, which is minted from verified
   * designer auth headers. On page refresh, userInfo/auth may not be populated
   * yet when the WS connects, so we poll briefly before registering.
   */
  private registerWithBridgeToken(attempt = 0): void {
    const userId = (window as any).userInfo?.id || undefined;
    if (!userId && attempt >= 20) {
      this.ws?.close();
      return;
    }

    if (!userId) {
      setTimeout(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.registerWithBridgeToken(attempt + 1);
        }
      }, 250);
      return;
    }

    const shouldForceRefresh = this.forceBridgeTokenRefresh;
    if (shouldForceRefresh) {
      this.forceBridgeTokenRefresh = false;
    }

    void this.getBridgeToken(shouldForceRefresh).then((bridgeToken) => {
      if (!bridgeToken) {
        if (attempt >= 20) {
          this.ws?.close();
          return;
        }
        setTimeout(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.registerWithBridgeToken(attempt + 1);
          }
        }, 250);
        return;
      }

      // Include domain hints so the server can attach the BalanceManager
      // subscription at register time — otherwise idle tabs (no chat sent
      // yet) miss cross-request balance updates from peer tabs.
      const domainName = (window as any).currentDomain || undefined;
      const authToken = localStorage.getItem("tokenString") || undefined;
      const projectId = (window as any).kuika?.activeProjectId || undefined;
      this.ws!.send(
        JSON.stringify({
          type: "register",
          role: "browser",
          tabId: McpToolBridge._tabId,
          bridgeToken,
          domainName,
          authToken,
          projectId
        })
      );
      this.onConnectionChange?.(true);
    });
  }

  private async getBridgeToken(forceRefresh = false): Promise<string | null> {
    const scopeKey = this.getBridgeTokenScopeKey();
    if (
      !forceRefresh &&
      this.bridgeToken &&
      this.bridgeToken.scopeKey === scopeKey &&
      this.bridgeToken.expiresAt - Date.now() > BRIDGE_TOKEN_REFRESH_SKEW_MS
    ) {
      return this.bridgeToken.token;
    }

    try {
      const headers = getRequestHeaders();
      const userName = (window as any).userInfo?.username || undefined;
      if (userName) {
        headers["x-user-name"] = userName;
      }

      const response = await axios.post(BRIDGE_TOKEN_URL, undefined, { headers });

      const data = response.data;
      if (typeof data?.bridgeToken !== "string" || typeof data?.expiresAt !== "number") return null;

      this.bridgeToken = {
        token: data.bridgeToken,
        expiresAt: data.expiresAt,
        scopeKey
      };
      return this.bridgeToken.token;
    } catch {
      return null;
    }
  }

  private getBridgeTokenScopeKey(): string {
    const headers = getRequestHeaders();
    return [headers["x-clientid"] || "-", headers.currentProjectId || "-", headers.domainName || "-"].join("|");
  }

  private getCachedBridgeToken(): string | null {
    const scopeKey = this.getBridgeTokenScopeKey();
    if (this.bridgeToken && this.bridgeToken.scopeKey === scopeKey && this.bridgeToken.expiresAt > Date.now()) {
      return this.bridgeToken.token;
    }
    return null;
  }

  private scheduleReconnect(): void {
    if (this.isDestroyed) return;

    this.reconnectTimer = setTimeout(() => {
      // reconnecting
      this.connect();
    }, this.reconnectDelay);

    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, MAX_RECONNECT_DELAY);
  }

  private handleMessage(message: WsBridgeMessage): void {
    if (message.type === "tool_execute") {
      this.executeAndRespond(message);
    } else if (message.type === "tool_batch_execute") {
      this.executeBatchAndRespond(message);
    } else if (message.type === "get_auth_headers") {
      this.respondWithAuthHeaders(message);
    } else if (message.type === "refresh_auth_token") {
      this.refreshAndRespondWithHeaders(message);
    } else if (message.type === "ping") {
      this.send({ type: "pong", requestId: message.requestId });
    } else if (message.type === "chat_attach_result") {
      this.onChatAttachResult?.(message.attachResults ?? []);
    } else if (message.type === "chat_event") {
      this.handleChatEvent(message);
    } else if (message.type === "chat_clear_result") {
      this.handleChatEvent(message);
    } else if (message.type === "chat_cancel_result") {
      this.handleChatEvent(message);
    } else if (message.type === "chat_title_result") {
      this.handleChatEvent(message);
    } else if (message.type === "prompt_improve_chunk") {
      this.handleChatEvent(message);
    } else if (message.type === "prompt_improve_done") {
      this.handleChatEvent(message);
    } else if (message.type === "prompt_improve_error") {
      this.handleChatEvent(message);
    } else if (message.type === "balance_state") {
      this.handleBalanceState(message);
    }
  }

  private balanceStateCallbacks = new Set<BalanceStateCallback>();
  private latestBalanceSnapshot: BalanceStateSnapshot | null = null;

  /**
   * Subscribe to authoritative balance snapshots for the user's domain.
   * Fires on every server-side change (reservation, step delta, finalize,
   * cancel, reconcile, periodic sync). Subscribers receive the full
   * snapshot — no delta math, no grace windows. New subscribers
   * immediately receive the most recent cached snapshot if one exists,
   * so a late-mounting UI doesn't wait for the next tick to render.
   */
  onBalanceState(callback: BalanceStateCallback): () => void {
    this.balanceStateCallbacks.add(callback);
    if (this.latestBalanceSnapshot) {
      try {
        callback(this.latestBalanceSnapshot);
      } catch {
        // Initial replay must never poison registration.
      }
    }
    return () => this.balanceStateCallbacks.delete(callback);
  }

  private handleBalanceState(message: WsBridgeMessage): void {
    const data = message.data as BalanceStateSnapshot | undefined;
    if (!data || typeof data.version !== "number") return;
    if (!isFresherBalanceSnapshot(this.latestBalanceSnapshot, data)) return;
    this.latestBalanceSnapshot = data;
    for (const cb of this.balanceStateCallbacks) {
      try {
        cb(data);
      } catch {
        // Subscriber failure must not block peers.
      }
    }
  }

  private handleChatEvent(message: WsBridgeMessage): void {
    // Intercept backend tool_activity events directly at the bridge level
    // to dispatch notifications before any chat panel filtering/dedup.
    if (message.event === "tool_activity" && message.data?.toolName) {
      processRawToolActivity(message.data);
      // KOA-17717 — backend tool activity emitted purely to drive the toolbar /
      // datasource-panel reveal animations + tooltips is tagged
      // `_notificationOnly`. It must NOT become a transcript pill: the backend
      // work already shows as a single Backend-Architect specialist entry, so a
      // per-tool pill would be noise that wasn't there before.
      //
      // Dropping it here also made it invisible to the waiting indicator, which
      // is why the slowest phase in the product showed the least: 2,514
      // database_schema calls averaging 7.8s and 494 db_sql_action calls
      // averaging 23.4s all ran under a frozen "Backend Architect". The event is
      // now forwarded so it can drive the header line, and the transcript is
      // protected at `dispatchStreamToolActivity`, which refuses the tag — no
      // pill is created, exactly as KOA-17717 intended.
    }

    const callback = this.chatEventCallbacks.get(message.requestId);
    if (callback) {
      callback({
        event: message.event || message.type,
        data: message.data || { cleared: message.cleared, cancelled: message.cancelled },
        requestId: message.requestId
      });
    }
  }

  // ── Chat methods ──

  /**
   * @returns false when the socket is not OPEN — caller must fail visibly;
   * silent drop used to leave the chat tab spinning forever.
   */
  sendChatRequest(requestId: string, payload: ChatRequestPayload): boolean {
    const cachedBridgeToken = this.getCachedBridgeToken();
    const enrichedPayload = {
      ...payload,
      ...(cachedBridgeToken ? { bridge_token: cachedBridgeToken } : {})
    };

    return this.send({
      type: "chat_request",
      requestId,
      payload: enrichedPayload
    });
  }

  sendChatCancel(requestId: string, force = false): void {
    this.send({
      type: "chat_cancel",
      requestId,
      ...(force ? { payload: { force: true } } : {})
    });
  }

  /**
   * Claim the conversations this browser's chat tabs own, so in-flight turns
   * keep narrating into THIS socket after a reconnect (KOA-18210).
   *
   * The server addresses a turn's events by `sessionId` — the durable id the
   * panel restores with each chat tab — not by the socket that opened the turn.
   * Without this claim a surviving turn keeps writing to the dead socket: the
   * thinking step disappears and `done` never arrives, while the build runs on.
   */
  sendChatAttach(claims: ChatAttachClaim[]): void {
    if (claims.length === 0) return;
    this.send({
      type: "chat_attach",
      requestId: `attach_${Date.now()}`,
      attach: claims
    });
  }

  /** Register the handler for `chat_attach_result`. Replaces any previous one. */
  setOnChatAttachResult(callback: (outcomes: ChatAttachOutcome[]) => void): void {
    this.onChatAttachResult = callback;
  }

  sendChatClear(requestId: string, sessionId: string): void {
    this.send({
      type: "chat_clear",
      requestId,
      payload: { session_id: sessionId, message: "" }
    });
  }

  sendInterruptResponse(
    requestId: string,
    sessionId: string,
    decision: InterruptDecision,
    answers?: string,
    toolCallId?: string
  ): void {
    this.send({
      type: "chat_interrupt_response",
      requestId,
      payload: { session_id: sessionId },
      data: {
        decision,
        ...(answers ? { answers } : {}),
        // `toolCallId` identifies which `state.pendingInterrupts[id]`
        // entry this decision resolves. Backend falls back to "the only
        // entry" when omitted — today's serial specialist pipeline only
        // ever produces one pending. Required once parallel specialist
        // execution is enabled on the backend and multiple pendings can
        // coexist in the same session.
        ...(toolCallId ? { toolCallId } : {})
      }
    });
  }

  sendGenerateTitle(
    requestId: string,
    message: string,
    chatMode?: "designer" | "builder" | "ask",
    context?: AuxCallContext
  ): void {
    this.send({
      type: "chat_generate_title",
      requestId,
      // chatMode lets the server skip billing for FREE modes (ASK).
      payload: { message, ...(chatMode ? { chatMode } : {}), ...buildAuxPayload(context) }
    });
  }

  sendPromptImprove(
    requestId: string,
    prompt: string,
    chatMode: "designer" | "builder" | "ask",
    context?: AuxCallContext
  ): void {
    this.send({
      type: "prompt_improve",
      requestId,
      payload: { prompt, chatMode, ...buildAuxPayload(context) }
    });
  }

  sendPromptImproveCancel(requestId: string): void {
    this.send({
      type: "prompt_improve_cancel",
      requestId
    });
  }

  onChatEvent(requestId: string, callback: ChatEventCallback): void {
    this.chatEventCallbacks.set(requestId, callback);
  }

  offChatEvent(requestId: string): void {
    this.chatEventCallbacks.delete(requestId);
  }

  // ── Auth headers ──

  private respondWithAuthHeaders(message: WsBridgeMessage): void {
    const headers = getRequestHeaders();
    this.send({
      type: "tool_result",
      requestId: message.requestId,
      result: headers,
      isError: false
    });
  }

  private refreshAndRespondWithHeaders(message: WsBridgeMessage): void {
    refreshAuthAndGetHeaders().then((headers) => {
      this.send({
        type: "tool_result",
        requestId: message.requestId,
        result: headers,
        isError: false
      });
    });
  }

  // ── Tool execution ──

  private executeAndRespond(message: WsBridgeMessage): void {
    const { requestId, toolName, input } = message;

    if (!toolName) {
      this.send({
        type: "tool_result",
        requestId,
        result: "No tool name provided",
        isError: true
      });
      return;
    }

    const toolInput = input || {};

    // Server-emitted infrastructure probes (root-empty checks before
    // ui_render, post-Designer validators, post-Binder completeness
    // probes, etc.) ride on the same tool_execute channel as
    // LLM-emitted calls. The LLM didn't ask for them — they're the
    // runtime's own paranoia — so they shouldn't surface in the chat
    // panel as user-visible "Inspecting component" / "Searching by
    // type" pills. Strip the marker before the handler sees it (every
    // Kuika handler ignores unknown input keys, but defensive
    // stripping keeps the contract clean) and skip
    // dispatchToolActivity entirely. Zone activation also short-
    // circuits — these probes never mutate the canvas.
    const isInfraCall = (toolInput as Record<string, unknown>)._infraCall === true;
    if (isInfraCall) {
      delete (toolInput as Record<string, unknown>)._infraCall;
    }

    const zone = getToolZone(toolName, toolInput);
    const isMutating = zone !== null;

    if (zone && !isInfraCall) {
      dispatchZoneEvent(MCP_ZONE_ACTIVATE, zone);
    }

    if (!isInfraCall) {
      dispatchToolActivity({
        requestId,
        toolCallId: message.toolCallId || requestId,
        tabId: message.tabId,
        toolName,
        input: toolInput,
        status: "running",
        isMutating,
        timestamp: Date.now()
      });
    }

    // Send application-level keepalive pongs during async tool execution
    // to prevent heartbeat termination when the browser tab is throttled.
    // Driven from a Worker so the tick fires reliably even when the tab
    // is hidden — main-thread setInterval would be clamped to ≥1s.
    const stopKeepalive = createBackgroundInterval(10000, () => {
      this.send({ type: "pong", requestId: "keepalive" });
    });

    // Acquire a Web Lock to signal that this tab is doing work; some
    // browsers factor active locks into throttling decisions. The actual
    // background-tab fix lives in `yieldToPaint` below — locks alone do
    // not bypass visibility throttling.
    let releaseLock: (() => void) | null = null;
    if (typeof navigator !== "undefined" && navigator.locks) {
      const lockName = `mcp_tool_${requestId}`;
      navigator.locks.request(lockName, { mode: "exclusive" }, () => {
        return new Promise<void>((resolve) => {
          releaseLock = resolve;
        });
      });
    }

    // When _deferRender is set, suppress screen re-renders until the final call
    // of a ui_render batch. This prevents the agent iframe from hanging during
    // rapid sequential tool calls (add → configure → responsive → styles).
    // With chunked rendering, add-chunk calls arrive WITHOUT _deferRender so each
    // chunk triggers a render; subsequent style/configure calls ARE deferred.
    let isBatchEnd = false;
    const hadDeferFlag = !!toolInput._deferRender;
    if (toolInput._deferRender) {
      if (!(window as any).kuika?._mcpDeferRender) {
        // First deferred call in a new batch — reset the reveal collector
        pendingRevealIds = [];
        pendingUpdateIds = [];
        startRevealSafetyTimer();
      }
      (window as any).kuika = (window as any).kuika || {};
      (window as any).kuika._mcpDeferRender = true;
      delete toolInput._deferRender; // Don't pass to handler
    } else if ((window as any).kuika?._mcpDeferRender) {
      // Last call in batch — clear flag BEFORE execution so setScreenDesign propagates
      isBatchEnd = true;
      (window as any).kuika._mcpDeferRender = false;
    }

    // Non-deferred add calls (chunked rendering): flush reveal after execution
    const isNonDeferredAdd = !hadDeferFlag && toolName === "ui_component" && toolInput?.operation === "add";
    // Non-deferred style/configure calls: flush update highlight after execution
    const isNonDeferredUpdate =
      !hadDeferFlag &&
      !isBatchEnd &&
      ((toolName === "ui_style" && toolInput?.operation === "set") ||
        (toolName === "ui_component" && toolInput?.operation === "configure"));

    // Yield to the browser paint cycle before starting tool execution.
    // Without this, React updates the DOM with the "running" indicator but
    // the browser never paints that frame because the heavy sync
    // McpToolExecutor.execute calls immediately block the main thread.
    // When the tab is hidden, `yieldToPaint` falls back to a microtask —
    // rAF is paused in background tabs and would otherwise stall here
    // until the tab is foregrounded, breaching the server's tool timeout.
    yieldToPaint(() => {
      try {
        const result = McpToolExecutor.execute(toolName, toolInput);
        // Handle both sync and async results
        if (result && typeof result.then === "function") {
          result.then(
            (resolved: any) => {
              stopKeepalive();
              releaseLock?.();
              if (zone && !resolved?.error) triggerZonePulse(zone);
              collectRevealIds(toolName, toolInput, resolved);
              flushRevealAfterCall(isBatchEnd, isNonDeferredAdd || isNonDeferredUpdate);
              if (isBatchEnd) flushDeferredMcpSd();
              if (!isInfraCall) {
                dispatchToolActivity({
                  requestId,
                  toolCallId: message.toolCallId || requestId,
                  tabId: message.tabId,
                  toolName,
                  input: toolInput,
                  status: "completed",
                  isMutating,
                  timestamp: Date.now(),
                  result: resolved
                });
              }
              if (zone) dispatchZoneEvent(MCP_ZONE_DEACTIVATE, zone);
              const shouldRefreshContext =
                (isMutating && (isBatchEnd || isNonDeferredAdd || isNonDeferredUpdate)) ||
                movesCanvas(toolName, toolInput);
              const finalResolved = shouldRefreshContext ? attachRefreshedContext(resolved) : resolved;
              this.send({ type: "tool_result", requestId, result: finalResolved, isError: false });
            },
            (err: any) => {
              stopKeepalive();
              releaseLock?.();
              flushRevealAfterCall(isBatchEnd, isNonDeferredAdd || isNonDeferredUpdate);
              if (isBatchEnd) flushDeferredMcpSd();
              if (!isInfraCall) {
                dispatchToolActivity({
                  requestId,
                  toolCallId: message.toolCallId || requestId,
                  tabId: message.tabId,
                  toolName,
                  input: toolInput,
                  status: "error",
                  isMutating,
                  timestamp: Date.now(),
                  error: err.message || "Unknown error"
                });
              }
              if (zone) dispatchZoneEvent(MCP_ZONE_DEACTIVATE, zone);
              this.send({ type: "tool_result", requestId, result: err.message || "Unknown error", isError: true });
            }
          );
        } else {
          stopKeepalive();
          releaseLock?.();
          if (zone && !result?.error) triggerZonePulse(zone);
          collectRevealIds(toolName, toolInput, result);
          flushRevealAfterCall(isBatchEnd, isNonDeferredAdd || isNonDeferredUpdate);
          if (isBatchEnd) flushDeferredMcpSd();
          if (!isInfraCall) {
            dispatchToolActivity({
              requestId,
              toolCallId: message.toolCallId || requestId,
              tabId: message.tabId,
              toolName,
              input: toolInput,
              status: "completed",
              isMutating,
              timestamp: Date.now(),
              result
            });
          }
          if (zone) dispatchZoneEvent(MCP_ZONE_DEACTIVATE, zone);
          const shouldRefreshContextSync =
            (isMutating && (isBatchEnd || isNonDeferredAdd || isNonDeferredUpdate)) || movesCanvas(toolName, toolInput);
          const finalResult = shouldRefreshContextSync ? attachRefreshedContext(result) : result;
          this.send({ type: "tool_result", requestId, result: finalResult, isError: false });
        }
      } catch (e: any) {
        stopKeepalive();
        releaseLock?.();
        flushRevealAfterCall(isBatchEnd, isNonDeferredAdd || isNonDeferredUpdate);
        if (isBatchEnd) flushDeferredMcpSd();
        if (!isInfraCall) {
          dispatchToolActivity({
            requestId,
            toolCallId: message.toolCallId || requestId,
            tabId: message.tabId,
            toolName,
            input: toolInput,
            status: "error",
            isMutating,
            timestamp: Date.now(),
            error: e.message || "Unknown error"
          });
        }
        if (zone) dispatchZoneEvent(MCP_ZONE_DEACTIVATE, zone);
        this.send({
          type: "tool_result",
          requestId,
          result: e.message || "Unknown error",
          isError: true
        });
      }
    });
  }

  /**
   * Execute a batch of tools in parallel via Promise.allSettled.
   * Uses allSettled (not all) so a single tool failure returns an individual
   * error result instead of rejecting the entire batch.
   */
  private executeBatchAndRespond(message: WsBridgeMessage): void {
    const { requestId, tools } = message;

    if (!tools || !Array.isArray(tools) || tools.length === 0) {
      this.send({ type: "tool_batch_result", requestId, results: [] });
      return;
    }

    // Single keepalive ticker for the entire batch — Worker-driven so
    // the pong fires reliably even when this tab is in the background.
    const stopKeepalive = createBackgroundInterval(10000, () => {
      this.send({ type: "pong", requestId: "keepalive" });
    });

    // Single Web Lock for the entire batch
    let releaseLock: (() => void) | null = null;
    if (typeof navigator !== "undefined" && navigator.locks) {
      const lockName = `mcp_batch_${requestId}`;
      navigator.locks.request(lockName, { mode: "exclusive" }, () => {
        return new Promise<void>((resolve) => {
          releaseLock = resolve;
        });
      });
    }

    // Dispatch batch-level activity
    dispatchToolActivity({
      requestId,
      toolName: `batch(${tools.length})`,
      input: {},
      status: "running",
      isMutating: false,
      timestamp: Date.now()
    });

    // Yield to the browser paint cycle before starting synchronous tool
    // execution. Without this, React updates the DOM with the "running"
    // indicator but the browser never paints that frame because the heavy
    // sync McpToolExecutor.execute calls immediately block the main thread.
    // When the tab is hidden, `yieldToPaint` falls back to a microtask —
    // rAF is paused in background tabs and would otherwise stall the batch
    // until the tab is foregrounded, breaching the server's batch timeout.
    yieldToPaint(() => {
      const promises = tools.map((t) => {
        const toolInput = t.input || {};
        try {
          const result = McpToolExecutor.execute(t.toolName, toolInput);
          if (result && typeof result.then === "function") {
            return result.then(
              (r: any) => ({ requestId: t.requestId, result: r, isError: false }),
              (e: any) => ({ requestId: t.requestId, result: e.message || "Unknown error", isError: true })
            );
          }
          return Promise.resolve({ requestId: t.requestId, result, isError: false });
        } catch (e: any) {
          return Promise.resolve({ requestId: t.requestId, result: e.message || "Unknown error", isError: true });
        }
      });

      Promise.allSettled(promises).then((settled) => {
        stopKeepalive();
        releaseLock?.();

        const results = settled.map((s, i) =>
          s.status === "fulfilled"
            ? s.value
            : { requestId: tools[i].requestId, result: "Execution failed", isError: true }
        );

        // Collect reveal/update IDs from batch results
        for (let i = 0; i < results.length; i++) {
          if (!results[i].isError) {
            const t = tools[i];
            collectRevealIds(t.toolName, t.input || {}, results[i].result);
          }
        }
        flushRevealBatch();

        dispatchToolActivity({
          requestId,
          toolName: `batch(${tools.length})`,
          input: {},
          status: "completed",
          isMutating: false,
          timestamp: Date.now()
        });

        this.send({ type: "tool_batch_result", requestId, results });
      });
    });
  }

  /** @returns true if the frame was written to an OPEN socket. */
  private send(message: Partial<WsBridgeMessage>): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  disconnect(): void {
    this.isDestroyed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.chatEventCallbacks.clear();
  }
}

let bridgeInstance: McpToolBridge | null = null;

export function initMcpToolBridge(): McpToolBridge {
  if (!bridgeInstance) {
    bridgeInstance = new McpToolBridge();
    bridgeInstance.connect();
  }
  return bridgeInstance;
}

export function destroyMcpToolBridge(): void {
  if (bridgeInstance) {
    bridgeInstance.disconnect();
    bridgeInstance = null;
  }
}

export function getMcpBridgeInstance(): McpToolBridge | null {
  return bridgeInstance;
}
